import type { CSSProperties } from 'react';
import '../../styles/logo.css';

// The seven glowing nodes of the client's mark, as % of each image (measured from the artwork).
const NODES = {
  lockup: [
    [11.4, 13.5], [3.3, 37], [19.5, 37], [11.4, 50.5], [4.65, 74.5], [18, 74.5], [11.4, 87.5],
  ],
  mark: [
    [50, 13.7], [15.6, 37], [84, 37], [49, 50], [21.5, 74], [78, 74], [50, 88],
  ],
} as const;

/**
 * The client's logo with light on it: a breathing glow, a halo turning behind the mark, a shine that sweeps
 * across the letters, light running along the DAO lines and the nodes twinkling. The light layers are masked
 * by the artwork itself, so they only ever light the logo's own pixels. Reduced motion leaves the plain logo.
 */
export function Logo({ variant, size, className = '', alt = 'Syntra DAO' }: {
  variant: 'lockup' | 'mark';
  size: number; // the rendered width in px
  className?: string;
  alt?: string;
}) {
  const src = variant === 'lockup' ? '/logo-lockup.png' : '/logo-mark.png';
  const height = variant === 'lockup' ? Math.round((size * 192) / 817) : size;
  const style = { '--logo': `url(${src})`, width: size, height } as CSSProperties;
  return (
    <span className={`logo-fx logo-${variant} ${className}`} style={style} data-testid={`logo-${variant}`}>
      <span className="logo-halo" aria-hidden />
      <img src={src} alt={alt} width={size} height={height} draggable={false} />
      <span className="logo-light" aria-hidden>
        {variant === 'lockup' ? (
          <>
            <i className="logo-beam left" />
            <i className="logo-beam right" />
          </>
        ) : null}
      </span>
      <span className="logo-sparks" aria-hidden>
        {NODES[variant].map(([x, y], i) => (
          <i key={i} style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${(i * 0.53).toFixed(2)}s` }} />
        ))}
      </span>
    </span>
  );
}
